import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DatasetService } from './dataset.service';
import { environment } from 'src/environments/environment';
import { apiEndpoints } from 'src/app/api-endpoints';
import {
  QUICKBOOKS_SESSION_KEYS,
  QuickBooksCallbackRequest,
  QuickBooksCompanyInfo,
  QuickBooksConnectRequest,
  QuickBooksConnectResponse,
  QuickBooksConnection,
  QuickBooksDisconnectResponse,
  QuickBooksEntitiesResponse,
  QuickBooksPendingConnect,
  QuickBooksSetTargetDatabaseRequest,
  QuickBooksSyncResponse,
} from './models/quickbooks.models';

/**
 * QuickBooks connection management.
 *
 * The backend owns the OAuth handshake — tokens never reach the browser, and
 * every QuickBooks API call goes through our own API. This service only starts
 * the authorization, hands back what comes off the redirect, and drives the
 * per-connection actions.
 *
 * Errors are deliberately not swallowed here. Callers need the status code to
 * tell a refused state (400) from an expired connection (409) from Intuit being
 * unreachable (502), and each needs different words.
 */
@Injectable({
  providedIn: 'root'
})
export class QuickBooksService {
  private apiUrl = environment.apiConfig.baseUri + apiEndpoints.quickBooks;

  private apis = {
    connect: '/connect',
    callback: '/callback',
    setTargetDatabase: '/set-target-database',
    entities: '/entities',
    sync: '/sync',
    companyInfo: '/company-info',
    disconnect: '/disconnect',
  };

  constructor(
    private http: HttpClient,
    private datasetService: DatasetService
  ) {}

  /**
   * Starts an authorization. The returned URL must be navigated to, not
   * fetched — Intuit renders its own sign-in and consent pages.
   */
  connect(targetDatabaseIntegrationId: number | null): Observable<QuickBooksConnectResponse> {
    const request: QuickBooksConnectRequest = { targetDatabaseIntegrationId };

    return this.http.post<QuickBooksConnectResponse>(
      `${this.apiUrl}${this.apis.connect}`,
      request
    );
  }

  /**
   * Sets, changes, or clears where a connection's data lands. Passing null
   * clears it — which is also how a targeted database becomes deletable again,
   * since that foreign key is RESTRICT.
   */
  setTargetDatabase(
    connectionId: number,
    targetDatabaseIntegrationId: number | null
  ): Observable<unknown> {
    const request: QuickBooksSetTargetDatabaseRequest = { connectionId, targetDatabaseIntegrationId };

    return this.http.post(`${this.apiUrl}${this.apis.setTargetDatabase}`, request);
  }

  /**
   * Completes an authorization with the values Intuit put on the URL.
   *
   * Sent with `skipRetry` because the code is single-use: replaying it can turn
   * a recoverable failure into a permanently spent one.
   */
  completeAuthorization(request: QuickBooksCallbackRequest): Observable<QuickBooksConnection> {
    return this.http.post<QuickBooksConnection>(
      `${this.apiUrl}${this.apis.callback}`,
      request,
      { headers: { 'skipRetry': 'true' } }
    );
  }

  /**
   * What this connection can sync, and whether it can sync at all.
   *
   * Answered from local mappings and metadata — it costs no Intuit quota and
   * doesn't fail when QuickBooks is down, so it's safe to call whenever the
   * dialog opens.
   */
  getEntities(connectionId: number): Observable<QuickBooksEntitiesResponse> {
    return this.http.post<QuickBooksEntitiesResponse>(
      `${this.apiUrl}${this.apis.entities}`,
      { connectionId }
    );
  }

  /**
   * Pulls an entity from Intuit and lands it as a dataset in the connection's
   * target database.
   *
   * Sent with `skipRetry`: this is slow, consumes Intuit quota, and writes
   * customer data. An automatic replay after a gateway timeout would repeat all
   * of that unseen. Re-running upserts on the source id, so a *deliberate*
   * retry is safe — which is what the UI tells the user instead.
   */
  sync(connectionId: number, entity: string): Observable<QuickBooksSyncResponse> {
    return this.http.post<QuickBooksSyncResponse>(
      `${this.apiUrl}${this.apis.sync}`,
      { connectionId, entity },
      { headers: { 'skipRetry': 'true' } }
    ).pipe(
      // A sync creates or refreshes a dataset, so the cached dataset list is now
      // wrong. Invalidating here rather than in the calling dialog means any
      // future caller gets it too — the cache is only consulted when non-empty,
      // so a stale copy would otherwise survive until a full page reload.
      tap(() => this.datasetService.clearDatasetReferences())
    );
  }

  /**
   * Reads the company from Intuit. Doubles as a connection test: it exercises
   * the stored token, the refresh path, and the API in one call — and refreshes
   * the stored company name as a side effect.
   */
  getCompanyInfo(connectionId: number): Observable<QuickBooksCompanyInfo> {
    return this.http.post<QuickBooksCompanyInfo>(
      `${this.apiUrl}${this.apis.companyInfo}`,
      { connectionId }
    );
  }

  /** Revokes the grant at Intuit, then removes the connection. */
  disconnect(connectionId: number): Observable<QuickBooksDisconnectResponse> {
    return this.http.post<QuickBooksDisconnectResponse>(
      `${this.apiUrl}${this.apis.disconnect}`,
      { connectionId }
    );
  }

  // ============================================
  // In-flight authorization state
  // ============================================

  stashPendingConnect(pending: QuickBooksPendingConnect): void {
    sessionStorage.setItem(QUICKBOOKS_SESSION_KEYS.PENDING_CONNECT, JSON.stringify(pending));
  }

  readPendingConnect(): QuickBooksPendingConnect | null {
    const raw = sessionStorage.getItem(QUICKBOOKS_SESSION_KEYS.PENDING_CONNECT);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as QuickBooksPendingConnect;
      if (!Number.isFinite(parsed?.applicationId)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  clearPendingConnect(): void {
    sessionStorage.removeItem(QUICKBOOKS_SESSION_KEYS.PENDING_CONNECT);
  }
}
